import React, { useRef, useState } from 'react';
import { StyleSheet, TextStyle } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import { isWeb } from 'shared/constants/platform';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

type textAlignment = Pick<TextStyle, 'textAlign'>['textAlign'];
type textSize = 'base' | 'small' | 'xSmall';
export interface LinkButtonProps extends PressableAnalyticsProps {
  text: string;
  linkOnDark?: boolean;
  size: textSize;
  isDisabled?: boolean;
  numberOfLines?: number;
  textAlignment?: textAlignment;
  linkOnPress?: (...args: unknown[]) => unknown;
}

export const LinkButton = ({
  text,
  linkOnDark = false,
  size,
  isDisabled = false,
  numberOfLines,
  textAlignment = 'left',
  linkOnPress,
  analyticsId = '',
  customAttributes = null,
}: LinkButtonProps) => {
  const isVisitedState = useRef(false);
  const maxLines = numberOfLines ?? 0;
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [isVisited, setIsVisited] = useState(isVisitedState.current);

  const computedStyles = composeLinkStyles(isDisabled, linkOnDark, isVisited);
  const sizedLinkStyles: TextStyle[] = [
    { textAlign: textAlignment },
    computedStyles.copy,
    isFocused ? computedStyles.copyFocused : undefined,
    isHovered ? computedStyles.copyHover : undefined,
    isPressed ? computedStyles.copyPressed : undefined,
  ].filter(isNotNil);

  const linkButtonAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const handleOnPressDelay = () => {
    if (!linkOnPress) {
      return;
    }

    linkButtonAnalytics();
    linkOnPress?.();

    if (isWeb) {
      setIsFocused(false);
      setIsVisited(false);
    } else if (isPressed) {
      setTimeout(() => {
        setIsPressed(false);
        setIsFocused(false);
        setIsVisited(false);
      }, 100);
    }
  };

  return (
    <PressableWithDelay
      role="button"
      style={baseStyles.button}
      onHoverIn={() => {
        setIsHovered(true);
      }}
      onHoverOut={() => {
        setIsHovered(false);
      }}
      onBlur={() => {
        setIsFocused(false);
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      onPressIn={() => {
        setIsPressed(true);
      }}
      onPressOut={() => {
        setIsPressed(false);
        setIsVisited(true);
      }}
      onPress={handleOnPressDelay}
      disabled={isDisabled}
      aria-disabled={isDisabled}
    >
      <SizedLink
        size={size}
        numberOfLines={maxLines}
        style={sizedLinkStyles}
        text={text}
      />
    </PressableWithDelay>
  );
};

const SizedLink = ({ size, text, numberOfLines, style }: SizeLinkProps) => {
  switch (size) {
    case 'xSmall':
      return (
        <HyperlinkXsText numberOfLines={numberOfLines} style={style}>
          {text}
        </HyperlinkXsText>
      );
    case 'small':
      return (
        <HyperlinkSmText numberOfLines={numberOfLines} style={style}>
          {text}
        </HyperlinkSmText>
      );
    default:
      return (
        <HyperlinkBaseText numberOfLines={numberOfLines} style={style}>
          {text}
        </HyperlinkBaseText>
      );
  }
};

interface ComposableStyle {
  copy: TextStyle;
  copyFocused?: TextStyle;
  copyHover?: TextStyle;
  copyPressed?: TextStyle;
}

const styles = StyleSheet.create({
  copy: {
    color: theme.color.interactive.primary,
  },
  copyFocused: {
    color: theme.color.border.focused,
  },
  copyHover: {
    color: theme.color.interactive.primaryHovered,
  },
  copyPressed: {
    color: theme.color.interactive.primaryPressed,
  },
});

const disabledStyles = StyleSheet.create({
  copy: {
    color: theme.color.interactive.disabled.text,
  },
});

const visitedStyles = StyleSheet.create({
  copy: {
    color: theme.color.interactive.black,
  },
});

const visitedOnDarkStyles = StyleSheet.create({
  copy: {
    color: theme.color.interactive.inverse,
  },
});

const onDarkStyles = StyleSheet.create({
  copy: {
    color: theme.color.interactive.inverse,
  },
  copyFocused: {
    color: theme.color.border.focusedInverse,
  },
  copyHover: {
    color: theme.color.interactive.inverseHovered,
  },
  copyPressed: {
    color: theme.color.interactive.inversePressed,
  },
});

const disabledOnDarkStyles = StyleSheet.create({
  copy: {
    color: '#ffffff66',
  },
});

const baseStyles = StyleSheet.create({
  button: {
    backgroundColor: 'transparent',
    width: 'auto',
  },
});

interface SizeLinkProps {
  size: textSize;
  text: string;
  numberOfLines: number;
  style: TextStyle[];
}

const composeLinkStyles = (
  linkIsDisabled: boolean,
  linkOnDark: boolean,
  isVisited: boolean,
): ComposableStyle => {
  if (linkIsDisabled) {
    if (linkOnDark) {
      return {
        copy: disabledOnDarkStyles.copy,
      };
    }
    return {
      copy: disabledStyles.copy,
    };
  }
  if (isVisited) {
    if (linkOnDark) {
      return {
        copy: visitedOnDarkStyles.copy,
      };
    }
    return {
      copy: visitedStyles.copy,
    };
  }
  if (linkOnDark) {
    return {
      copy: onDarkStyles.copy,
      copyFocused: onDarkStyles.copyFocused,
      copyHover: onDarkStyles.copyHover,
      copyPressed: onDarkStyles.copyPressed,
    };
  }
  return {
    copy: styles.copy,
    copyFocused: styles.copyFocused,
    copyHover: styles.copyHover,
    copyPressed: styles.copyPressed,
  };
};

export default LinkButton;
